'use client'
import React, { memo, useMemo, useCallback, useRef, useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { Stack, Typography, MenuItem, InputAdornment, Checkbox, ToggleButton, ToggleButtonGroup, useTheme, FormControlLabel, IconButton, Autocomplete as MuiAutocomplete, createFilterOptions } from '@mui/material'
import { DesktopDatePicker as DatePicker, DesktopDatePicker as MonthYearPicker, DesktopDateTimePicker, MobileDateTimePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import Switch from 'src/components/era-package/era/Switch'
import TextField from 'src/components/era-package/era/DebouncedTextField'
import Autocomplete from './AltAutocomplete'
import { format, isDate, isValid } from 'date-fns'
import Grid from '@mui/material/Unstable_Grid2'
import { useFormConfigContext } from './hooks/useFormConfig'
import { DateRangePicker } from './DateRangePicker'
import { formatText } from 'src/utils/era-package'
import FileUploadModal from './FileUploadModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperclip, faSearch } from '@fortawesome/pro-regular-svg-icons'
import AbiTradePartnerApiUrls from '../../../apiUrls/AbiTradePartnerApiUrls'
import AbiSKUMgmtApiUrls from '../../../apiUrls/AbiSKUMgmtApiUrls'
import { FileForUserGetPartyId } from '../../../common/FileForUserCommon'

const toInputString = (v) => (v == null ? '' : (typeof v === 'string' ? v : String(v)))
// Server-side lookup autocomplete component for Party Lookup
const ServerSideLookupAutocomplete = ({
  field,
  fieldValue,
  fieldError,
  isRequired,
  dense,
  currentHandlers,
  isUsingRHF,
  formContext,
  setHasUnsaved
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const debounceRef = useRef(null)

  // Fetch options from API based on search text
  const fetchOptions = async (searchText) => {
    if (!searchText || searchText.length < 1) {
      setOptions([])
      return
    }

    const isSKULookup = field?.name?.endsWith("skuLookup")

    const baseUrl = isSKULookup ? AbiSKUMgmtApiUrls.GET : AbiTradePartnerApiUrls.GET

    setLoading(true)
    try {
      const response = await fetch(
        `${baseUrl}?partyId=${FileForUserGetPartyId()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          }
        }
      )

      if (response.ok) {
        const json = await response.json()
        const partners = json?.data || []
        
        // Filter by search text (case-insensitive)
        const searchLower = searchText.toLowerCase()
        if(isSKULookup) {
          const filtered = partners.filter(partner => 
            partner.skuReference?.toLowerCase().includes(searchLower) ||
            partner.skuDesc?.toLowerCase().includes(searchLower)
          )

          const mappedOptions = filtered.map(partner => ({
            value: partner.id?.toString() || partner.skuReference,
            label: partner.skuReference || partner.skuDesc,
            data: partner
          }))
          setOptions(mappedOptions)
        }
        else {
          const filtered = partners.filter(partner => 
            partner.partyMasterName?.toLowerCase().includes(searchLower) ||
            partner.name?.toLowerCase().includes(searchLower) ||
            partner.partyCode?.toLowerCase().includes(searchLower)
          )

          const mappedOptions = filtered.map(partner => ({
            value: partner.id?.toString() || partner.partyMasterName,
            label: partner.partyMasterName || partner.name,
            data: partner
          }))
          setOptions(mappedOptions)
        }
      } else {
        setOptions([])
      }
    } catch (error) {
      console.error('Error fetching trade partners:', error)
      setOptions([])
    } finally {
      setLoading(false)
    }
  }

    // Debounced input change handler
  const handleInputChange = (event, newInputValue, reason) => {
    // Only update input value when user is typing, not when selecting
    if (reason === 'input') {
      setInputValue(newInputValue)
      
      // Clear previous debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      
      // Debounce API call (500ms)
      debounceRef.current = setTimeout(() => {
        fetchOptions(newInputValue)
      }, 500)
    } else if (reason === 'reset' || reason === 'clear') {
      // When selection is made or cleared, keep input empty
      setInputValue('')
    }
  }

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <MuiAutocomplete
      freeSolo
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option
        return option?.label || ''
      }}
            value={null}
      onChange={(event, newValue) => {
        if (newValue?.data) {
          // Trigger the lookup select callback to auto-populate fields
          if (field.onLookupSelect) {
            field.onLookupSelect(newValue.data)
          }
          // Clear the input after selection
          setInputValue('')
          setOptions([])
        }
      }}
      disabled={field.disabled || false}
      size={field.size || (dense ? 'small' : 'medium')}
      filterOptions={(x) => x} // Disable built-in filtering since we filter server-side
      renderInput={(params) => (
        <TextField
          {...params}
          name={field.name}
          label={field.label}
          placeholder={field.placeholder || `Type to search...`}
          required={isRequired}
          error={!!fieldError}
          helperText={fieldError}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
                <InputAdornment position="end">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      field.onLookupClick && field.onLookupClick()
                    }}
                    edge="end"
                    title="Open Trade Partner List"
                    size="small"
                    disabled={field.disabled || false}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </IconButton>
                </InputAdornment>
              </>
            ),
          }}
        />
      )}
    />
  )
}

const filter = createFilterOptions()

const RenderFormFields = memo(
  ({ fields, arrayKey, optionsMap, formData, setFormData, forceFormData, errors, setErrors, setHasUnsaved, readOnly = false, dense, spacing = 2.5 }) => {
    const dateFormatInput = 'MM-dd-yyyy'
    const dateTimeFormatInput = 'MM-dd-yyyy HH:mm'
    const monthYearFormatInput = 'MM yyyy'
    const dateFormatDisplay = 'MMM-dd-yyyy'
    const theme = useTheme()
    const [fileUpload, setFileUpload] = useState(null)

    // Check if we're using React Hook Form (FormProvider context exists)
    let formContext = null
    try {
      formContext = useFormConfigContext()
    } catch (e) {
      // No FormProvider context, use legacy mode
    }

    const isUsingRHF = !!formContext

    // Legacy handlers (used when not in React Hook Form context)
    const handleInput = useCallback(
      (field) => (event) => {
        if (isUsingRHF) return // Skip if using RHF

        const value = event.target.value
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }))

        // Set unsaved changes flag
        if (setHasUnsaved) {
          setHasUnsaved(true)
        }

        // Clear error when user starts typing
        if (setErrors && errors?.[field]) {
          setErrors((prev) => ({
            ...prev,
            [field]: '',
          }))
        }
      },
      [setFormData, setErrors, errors, setHasUnsaved, isUsingRHF],
    )

    const handleInputAuto = useCallback(
      (field) => (event, value) => {
        if (isUsingRHF) return // Skip if using RHF

        const normalizeOptionValue = (option) => {
          if (typeof option === 'string') return option
          return option?.value || ''
        }

        const normalizedValue = Array.isArray(value)
          ? value.map((option) => normalizeOptionValue(option)).filter(Boolean)
          : normalizeOptionValue(value)

        setFormData((prev) => ({
          ...prev,
          [field]: normalizedValue,
        }))

        // Set unsaved changes flag
        if (setHasUnsaved)
          setHasUnsaved(true)

        // Clear error when user starts typing
        if (setErrors && errors?.[field])
          setErrors((prev) => ({
            ...prev,
            [field]: '',
          }))
      },
      [setFormData, setErrors, errors, setHasUnsaved, isUsingRHF],
    )

    const handleInputBoolean = useCallback(
      (field) => (event, checked) => {
        if (isUsingRHF) return // Skip if using RHF

        // Handle both ToggleButtonGroup and Switch events
        const value =
          checked !== undefined
            ? checked
            : event.target.value === 'true' || event.target.value === true

        setFormData((prev) => ({
          ...prev,
          [field]: Boolean(value),
        }))

        // Set unsaved changes flag
        if (setHasUnsaved) {
          setHasUnsaved(true)
        }

        // Clear error when user starts typing
        if (setErrors && errors?.[field]) {
          setErrors((prev) => ({
            ...prev,
            [field]: '',
          }))
        }
      },
      [setFormData, setErrors, errors, setHasUnsaved, isUsingRHF],
    )

    // Store timeout refs for debouncing date inputs
    const dateTimeoutsRef = useRef({})

    const handleInputDate = useCallback(
      (field) => (date) => {
                if (isUsingRHF) return // Skip if using RHF
        // Convert date to ISO string format (yyyy-MM-dd) or empty string if null
        const value = date && isValid(date) ? format(date, 'yyyy-MM-dd') : ''

        // Clear any existing timeout for this field
        if (dateTimeoutsRef.current[field]) {
          clearTimeout(dateTimeoutsRef.current[field])
        }

        // Debounce the state updates to improve performance during typing
        dateTimeoutsRef.current[field] = setTimeout(() => {
          setFormData((prev) => ({
            ...prev,
            [field]: value,
          }))

          // Set unsaved changes flag
          if (setHasUnsaved) {
            setHasUnsaved(true)
          }

          // Need to check if what the user key in is valid        
          if (value === 'Invalid Date') {
            if (setErrors) {
              setErrors((prev) => ({
                ...prev,
                [field]: value,
              }))
            }
          } else {
            if (setErrors && errors?.[field]) {
              setErrors((prev) => ({
                ...prev,
                [field]: '',
              }))
            }
          }

          // Clean up the timeout reference
          delete dateTimeoutsRef.current[field]
        }, 300)
      },
      [setFormData, setErrors, errors, setHasUnsaved, isUsingRHF],
    )

    const handleInputDateTime = useCallback(
      (field) => (date) => {
                if (isUsingRHF) return // Skip if using RHF
        // Convert date to ISO string format (yyyy-MM-dd) or empty string if null
        const value = date && isValid(date) ? format(date, 'yyyy-MM-dd HH:mm') : ''

        // Clear any existing timeout for this field
        if (dateTimeoutsRef.current[field]) {
          clearTimeout(dateTimeoutsRef.current[field])
        }

        // Debounce the state updates to improve performance during typing
        dateTimeoutsRef.current[field] = setTimeout(() => {
          setFormData((prev) => ({
            ...prev,
            [field]: value,
          }))

          // Set unsaved changes flag
          if (setHasUnsaved) {
            setHasUnsaved(true)
          }

          // Need to check if what the user key in is valid
          if (value === 'Invalid Date') {
            if (setErrors) {
              setErrors((prev) => ({
                ...prev,
                [field]: value,
              }))
            }
          } else {
            if (setErrors && errors?.[field]) {
              setErrors((prev) => ({
                ...prev,
                [field]: '',
              }))
            }
          }

          // Clean up the timeout reference
          delete dateTimeoutsRef.current[field]
        }, 300)
      },
      [setFormData, setErrors, errors, setHasUnsaved, isUsingRHF],
    )

    const handleFile = (selectedFile, field) => {
      console.log('selFile', selectedFile, typeof selectedFile, field)
      if (isUsingRHF) {
        formContext.setValue(field.name, selectedFile, { shouldValidate: true, shouldDirty: true })
        if (setHasUnsaved) setHasUnsaved(true)
      } else {
        setFormData((prev) => ({
          ...prev,
          [field.name]: selectedFile,
        }))

        // Set unsaved changes flag
        if (setHasUnsaved) {
          setHasUnsaved(true)
        }

        // Clear error when user starts typing
        if (setErrors && errors?.[field]) {
          setErrors((prev) => ({
            ...prev,
            [field]: '',
          }))
        }
      }

    }

    // Cleanup timeouts on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        Object.values(dateTimeoutsRef.current).forEach(timeoutId => {
          clearTimeout(timeoutId)
        })
        dateTimeoutsRef.current = {}
      }
    }, [])

    const handleDateRange = useCallback(
      (field) => (range) => {
        if (isUsingRHF) return // Skip if using RHF

        setFormData((prev) => ({
          ...prev,
          [field]: range,
        }))

        // Set unsaved changes flag
        if (setHasUnsaved) {
          setHasUnsaved(true)
        }

        // Clear error when user starts typing
        if (setErrors && errors?.[field]) {
          setErrors((prev) => ({
            ...prev,
            [field]: '',
          }))
        }
      },
      [setFormData, setErrors, errors, setHasUnsaved, isUsingRHF],
    )

    // Helper function to render a single field with React Hook Form support
    const renderField = useCallback((field) => {
      // For React Hook Form mode, we'll handle controllers differently
      // to avoid calling hooks inside callbacks
      let currentFormData = formData
      let currentErrors = errors
      let currentHandlers = {
        handleInput: handleInput(field.name),
        handleInputAuto: handleInputAuto(field.name),
        handleInputBoolean: handleInputBoolean(field.name),
        handleInputDate: handleInputDate(field.name),
        handleInputDateTime: handleInputDateTime(field.name),
        handleDateRange: handleDateRange(field.name)
      }

      // In RHF mode, we'll use a different approach that doesn't call hooks in callbacks
      if (isUsingRHF && !forceFormData && formContext) {
        const fieldValue = formContext.watch(field.name)
        const fieldError = formContext.getError(field.name) || ''

        currentFormData = { [field.name]: fieldValue }
        currentErrors = { [field.name]: fieldError }

        // Create RHF-compatible handlers
        currentHandlers = {
          handleInput: (event) => {
            formContext.setValue(field.name, event.target.value, { shouldValidate: true, shouldDirty: true })
            if (setHasUnsaved) setHasUnsaved(true)
          },
          handleInputAuto: (event, value) => {
            const normalizeOptionValue = (option) => {
              if (typeof option === 'string') return option
              return option?.value || ''
            }

            if(Array.isArray(value)){
              const selectedValues = value.map(option => normalizeOptionValue(option)).filter(Boolean)
              formContext.setValue(field.name, selectedValues || [], { shouldValidate: true, shouldDirty: true })
            } else {
              formContext.setValue(field.name, normalizeOptionValue(value), { shouldValidate: true, shouldDirty: true })
            }
            if (setHasUnsaved) setHasUnsaved(true)
          },
          handleInputBoolean: (event, checked) => {
            const value = checked !== undefined ? checked : event.target.value === 'true' || event.target.value === true
            formContext.setValue(field.name, Boolean(value), { shouldValidate: true, shouldDirty: true })
            if (setHasUnsaved) setHasUnsaved(true)
          },
                      handleInputDate: (date) => {
              const value = date && isValid(date) ? format(date, 'yyyy-MM-dd') : ''
            formContext.setValue(field.name, value, { shouldValidate: true, shouldDirty: true })
            if (setHasUnsaved) setHasUnsaved(true)
          },
                      handleInputDateTime: (date) => {
              const value = date && isValid(date) ? format(date, 'yyyy-MM-dd HH:mm') : ''
            formContext.setValue(field.name, value, { shouldValidate: true, shouldDirty: true })
            if (setHasUnsaved) setHasUnsaved(true)
          },
          handleInputMonthYear: (date) => {
            const value = date && isValid(date) ? format(date, 'yyyy-MM') : ''
            formContext.setValue(field.name, value, { shouldValidate: true, shouldDirty: true })
            if (setHasUnsaved) setHasUnsaved(true)
          },

          handleDateRange: (range) => {
            formContext.setValue(field.name, range, { shouldValidate: true, shouldDirty: true })
            if (setHasUnsaved) setHasUnsaved(true)
          }
      }
      }

      const isDirty = isUsingRHF
        ? formContext?.formState.dirtyFields?.hasOwnProperty(field.name)
        : false
      const toggleLabels = (Array.isArray(field.toggleLabels) && field.toggleLabels.length === 2)
        ? field.toggleLabels : ['Yes', 'No']

      const type = field.type.trim().toLowerCase()
      const sizeProp = field.sizeProp || { xs: 12, sm: 6, lg: 4, xl: 3 }
      const fieldValue = currentFormData[field.name]
      let fieldError = !isUsingRHF
        ? null // if field is clean, don't display error state
        : (isDirty || formContext.formState.isSubmitted) ? currentErrors[field.name] : null

      // Check for conditional required (requiredCondition)
      let isRequired = field.required || false
      if (field.requiredCondition && typeof field.requiredCondition === 'function') {
        isRequired = field.requiredCondition(
          isUsingRHF ? formContext.getValues() : formData,
          formContext?.getValues(arrayKey) || null,
        )
      }

      if (readOnly || field.readOnly) {
        let value
        let otherGridProps = {}
        let typographyProps = {
          color: theme.palette.text.primary
        }
        if (!(type === 'textarea' || type === 'response')) {
          otherGridProps = {
            sm: 6,
            lg: 4,
            xl: 3
          }
        }
        if (['checkbox', 'toggle', 'switch'].includes(type)) {
          value = fieldValue ? toggleLabels[0] : toggleLabels[1]
        } else if (type === 'select' || type.startsWith('auto')) {
          const selectedOption = optionsMap[field.name]?.find(option => option.value === fieldValue)
          if (selectedOption) {
            if (type === 'auto value') {
              value = selectedOption.value
            } else if (type === 'auto description') {
              value = selectedOption.description
            } else { // select | auto
              value = `${selectedOption.value} - ${selectedOption.description}`
            }
          } else {
            value = '-'
          }
        } else if (type === 'date' || type === 'datetime') {
          if (isDate(fieldValue))
            value = format(fieldValue, dateFormatDisplay)
          else if (field.dateFormats)
            value = formatText(fieldValue, 'date', field.dateFormats)
          else {
            if (typeof fieldValue === 'string') {
              value = fieldValue?.split('-')
              if (value.length === 3) {
                value[1]-- // fix for 0-indexed months
                value = format(new Date(...value), dateFormatDisplay)
              }
            }

            if (!value)
              value = fieldValue
                ? format(fieldValue, dateFormatDisplay)
                : ''
          }
        } else if (type === 'textarea') {
          value = fieldValue?.trim() || '-'
          typographyProps = {
            ...typographyProps,
            component: "pre",
            sx: {
              whiteSpace: 'pre-wrap',
              backgroundColor: 'unset!important',
            },
          }
        } else if (type === 'response') {
          value = fieldValue?.trim() || '-'
          typographyProps = {
            ...typographyProps,
            component: 'pre',
            fontFamily: 'monospace',
            sx: {
              border: `1px solid ${theme.palette.divider}`,
              padding: 2,
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              backgroundColor: 'unset!important',
            },
          }
        } else if (type === 'status') {
          if (typeof fieldValue === "object") {
            value = fieldValue?.value || '-'
            let color
            switch (fieldValue?.color) {
              case 'info':
                color = theme.palette.info.main
                break
              case 'success':
                color = theme.palette.success.main
                break
              case 'warning':
                color = theme.palette.warning.main
                break
              case 'error':
                color = theme.palette.error.main
                break
              default:
                color = theme.palette.text.main
            }
            typographyProps = {
              color: color,
              fontWeight: 600,
            }
          } else {
            value = fieldValue || '-'
          }
        } else {
          value = fieldValue || '-'
        }

        // Final display formatting
        if (typeof field.formatter === 'string')
          value = formatText(value, field.formatter, field.formatDef)
        else if (typeof field.formatter === 'function')
          value = field.formatter(value)


        return (
          <Grid key={field.name} xs={12} {...otherGridProps}>
            <Typography
              fontSize="0.875rem"
              fontWeight={500}
              color={theme.palette.text.primary}
              sx={{
                opacity: 0.6,
              }}
            >
              {field.label}
            </Typography>
            <Typography {...typographyProps}>
              {value}
            </Typography>
          </Grid>
        )
      }

      // Render editable fields
      if (type === 'checkbox') {
        return (
          <Grid key={field.name} {...sizeProp} >
            <FormControlLabel
              control={
            <Checkbox
              name={field.name}
              checked={fieldValue || false}
              disabled={field.disabled || false}
              onChange={currentHandlers.handleInputBoolean}
              size={field.size || (dense ? 'small' : 'medium')}
            />
              }
              label={field.label}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
                mt: 1,
              }}
            />
          </Grid>
        )
      } else if (type === 'toggle') {
        return (
          <Grid key={field.name} {...sizeProp}>
            <Typography
              component="label"
              display="block"
              mb="5px"
              lineHeight="1.4375em"
              fontSize="0.875rem"
              fontWeight={500}
              color={field.disabled && 'rgba(0, 0, 0, 0.38)'}
            >
              {field.label}
            </Typography>
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={fieldValue}
              disabled={field.disabled || false}
              onChange={currentHandlers.handleInputBoolean}
              fullWidth
              size={field.size || (dense ? 'small' : 'medium')}
            >
              <ToggleButton value={true} sx={{ width: '50%' }}>
                {toggleLabels[0]}
              </ToggleButton>
              <ToggleButton value={false} sx={{ width: '50%' }}>
                {toggleLabels[1]}
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        )
      } else if (type === 'switch') {
        return (
          <Grid key={field.name} {...sizeProp}>
            <Typography
              component="label"
              display="block"
              mb="5px"
              lineHeight="1.4375em"
              fontSize="0.875rem"
              fontWeight={500}
              color={field.disabled && 'rgba(0, 0, 0, 0.38)'}
            >
              {field.label}
            </Typography>
            <Switch
              name={field.name}
              checked={fieldValue || false}
              disabled={field.disabled || false}
              onChange={currentHandlers.handleInputBoolean}
              error={!!fieldError}
              sx={{ mt: '4px' }}
              size={field.size || (dense ? 'small' : 'medium')}
            />
          </Grid>
        )
      } else if (type === 'select') {
        return (
          <Grid key={field.name} {...sizeProp}>
            <TextField
              select
              name={field.name}
              label={field.label}
              placeholder={field.placeholder || `Please select ${field.label}`}
              value={toInputString(fieldValue)}
              required={isRequired}
              disabled={field.disabled || false}
              InputProps={
                field.icon && {
                  startAdornment: (
                    <InputAdornment position="start">{field.icon}</InputAdornment>
                  ),
                }
              }
              onChange={currentHandlers.handleInput}
              error={!!fieldError}
              helperText={fieldError}
              size={field.size || (dense ? 'small' : 'medium')}
            >
              {optionsMap[field.name]?.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.value} - {option.description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )
      } else if (type.startsWith('auto')) {
        const rawOptions = optionsMap[field.name] || []
        const options = rawOptions.filter(option => {
          if (!option || !option.parent)
            return true

          const arrayName = typeof arrayKey === 'string' ? arrayKey.split('.').at(-2) : undefined
          let currentArrayKey = arrayKey
          if (typeof arrayKey === 'string' && arrayName !== 'pgaList' && option.parent === 'programCode') {
            currentArrayKey = arrayKey.split('.').slice(0, -2).join('.')
          }
          const parentValue = formContext?.getValues(currentArrayKey)?.[option.parent]
          return parentValue === option.code
        }).map((option) => ({
          value: option.value,
          description:
            type === 'auto value'
              ? option.value
              : type === 'auto description'
                ? option.description
                : `${option.value} - ${option.description}`,
        }))

        const freeSolo = {}
        if (field.creatable) {
          freeSolo.creatable = field.creatable
          freeSolo.filterOptions = (options, params) => {
            const filtered = filter(options, params)

            if (params.inputValue !== '') {
              filtered.push({
                inputValue: params.inputValue,
                description: `Add "${params.inputValue}"`,
              })
            }

            return filtered
          }
        }

        return (
          <Grid key={field.name} {...sizeProp}>
            <Autocomplete
              name={field.name}
              label={field.label}
              placeholder={field.placeholder || `Please select ${field.label}`}
              value={field.multiple
                ? (options?.filter((option) => fieldValue?.includes(option.value)) || [])
                : options?.find((option) => option.value === fieldValue) || (field.creatable ? fieldValue || '' : '')}
              required={isRequired}
              disabled={field.disabled || false}
              onChange={currentHandlers.handleInputAuto}
              error={!!fieldError}
              helperText={fieldError}
              options={options || []}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option
                return option.description || option.inputValue || ''
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === 'string') {
                  return option?.value === value
                }
                return option?.value === value?.value
              }}
              size={field.size || (dense ? 'small' : 'medium')}
              multiple={field.multiple || false}
              disableCloseOnSelect={field.multiple || false}
              {...freeSolo}
            />
          </Grid>
        )
      } else if (type === 'textarea') {
        return (
          <Grid key={field.name} xs={12}>
            <TextField
              multiline
              name={field.name}
              label={field.label}
              type={type || 'text'}
              maxLength={field.maxLength || undefined}
              placeholder={
                field.placeholder ||
                (field.maxLength ? undefined : `Please enter ${field.label}`)
              }
              value={toInputString(fieldValue)}
              required={isRequired}
              disabled={field.disabled || false}
              onChange={currentHandlers.handleInput}
              error={!!fieldError}
              helperText={fieldError}
              size={field.size || (dense ? 'small' : 'medium')}
            />
          </Grid>
        )
      } else if (type === 'statictext') {
        return (
          <Grid key={field.name} xs={12}>
            {field.label && (
              <Typography
                component="label"
                display="block"
                mb="5px"
                lineHeight="1.4375em"
                fontSize="0.875rem"
                fontWeight={500}
                color={theme.palette.text.primary}
              >
                {field.label}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                p: 1.5,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: (theme) => theme.palette.action.hover,
                color: (theme) => theme.palette.text.secondary,
                fontSize: '1rem',
                lineHeight: 1.6,
              }}
            >
              {field.text}
            </Typography>
          </Grid>
        )
      } else if (type === 'date') {
        return (
          <Grid key={field.name} {...sizeProp}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={field.label}
                inputFormat={dateFormatInput}
                value={fieldValue ? new Date(fieldValue) : null}
                disabled={field.disabled || false}
                InputProps={
                  field.icon && {
                    startAdornment: (
                      <InputAdornment position="start">{field.icon}</InputAdornment>
                    ),
                  }
                }
                onChange={currentHandlers.handleInputDate}
                renderInput={({ error, ...params }) => (
                  <TextField
                    name={field.name}
                    required={isRequired}
                    error={error || !!fieldError}
                    helperText={fieldError}
                    size={field.size || (dense ? 'small' : 'medium')}
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        )
      } else if (type === 'monthyear') {
        return (
          <Grid key={field.name} {...sizeProp}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MonthYearPicker
                label={field.label}
                inputFormat={monthYearFormatInput}
                views={['month', 'year']}
                value={fieldValue ? new Date(fieldValue) : null}
                disabled={field.disabled || false}
                onChange={(date) => {
                  const value = date && isValid(date) ? format(date, 'yyyy-MM') : ''
                  if (isUsingRHF && formContext) {
                    formContext.setValue(field.name, value, { shouldValidate: true, shouldDirty: true })
                    if (setHasUnsaved) setHasUnsaved(true)
                  } else {
                    currentHandlers.handleInputDate(date)
                  }
                }}
                renderInput={({ error, ...params }) => (
                  <TextField
                    name={field.name}
                    required={isRequired}
                    error={error || !!fieldError}
                    helperText={fieldError}
                    size={field.size || (dense ? 'small' : 'medium')}
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        )
      } else if (type === 'datetime') {
        return (
          <Grid key={field.name} {...sizeProp}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDateTimePicker
                label={field.label}
                inputFormat={dateTimeFormatInput}
                ampm={false}
                value={fieldValue ? new Date(fieldValue) : null}
                disabled={field.disabled || false}
                onChange={currentHandlers.handleInputDateTime}
                renderInput={({ error, ...params }) => (
                  <TextField
                    name={field.name}
                    required={isRequired}
                    error={error || !!fieldError}
                    helperText={fieldError}
                    placeholder={field.placeholder || `Select ${field.label}`}
                    size={field.size || (dense ? 'small' : 'medium')}
                    InputProps={{
                      ...params.InputProps,
                      ...(field.icon && {
                        startAdornment: (
                          <InputAdornment position="start">{field.icon}</InputAdornment>
                        )
                      })
                    }}
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        )
      } else if (type === 'daterange') {
        return <Grid key={field.name} {...sizeProp}>
          <DateRangePicker
            name={field.name}
            label={field.label}
            placeholder={
              field.placeholder ||
              (field.maxLength ? undefined : `Please select a date range for ${field.label}`)
            }
            value={fieldValue}
            required={isRequired}
            disabled={field.disabled || false}
            error={!!fieldError}
            helperText={fieldError}
            onChange={currentHandlers.handleDateRange}
            size={field.size || (dense ? 'small' : 'medium')}
          />
        </Grid>
      } else if (type === 'file') {
        // file upload
        return (
          <Grid key={field.name} {...sizeProp}>
            {/**
             * TODO: button open file upload modal, or straight open file browser?
             */}
            <TextField readOnly
              name={field.name}
              label={field.label}
              placeholder={
                field.placeholder ||
                'Upload a file...'
              }
              value={fieldValue?.name || ''}
              required={isRequired}
              disabled={field.disabled || false}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><FontAwesomeIcon icon={faPaperclip} /></InputAdornment>
                ),
              }}
              onClick={() => setFileUpload(field)}
              error={!!fieldError}
              helperText={fieldError}
              size={field.size || (dense ? 'small' : 'medium')}
            />
          </Grid>
        )
                                                                                                                                                } else if (type === 'lookupbutton') {
        // Lookup field - server-side autocomplete with search button for popup
        // Uses field.lookupProps for API configuration
        return (
          <Grid key={field.name} {...sizeProp}>
            <ServerSideLookupAutocomplete
              field={field}
              fieldValue={fieldValue}
              fieldError={fieldError}
              isRequired={isRequired}
              dense={dense}
              currentHandlers={currentHandlers}
              isUsingRHF={isUsingRHF}
              formContext={formContext}
              setHasUnsaved={setHasUnsaved}
            />
          </Grid>
        )
      } else {
        // text | number
        return (
          <Grid key={field.name} {...sizeProp}>
            <TextField
              name={field.name}
              label={field.label}
              type={type || 'text'}
              maxLength={field.maxLength || undefined}
              placeholder={
                field.placeholder ||
                (field.maxLength ? undefined : `Please enter ${field.label}`)
              }
              value={toInputString(fieldValue)}
              required={isRequired}
              disabled={field.disabled || false}
              InputProps={
                field.icon && {
                  startAdornment: (
                    <InputAdornment position="start">{field.icon}</InputAdornment>
                  ),
                }
              }
              onChange={currentHandlers.handleInput}
              error={!!fieldError}
              helperText={fieldError}
              size={field.size || (dense ? 'small' : 'medium')}
            />
          </Grid>
        )
      }
    }, [dense, formData, errors, optionsMap, handleInput, handleInputAuto, handleInputBoolean, handleInputDate, handleInputDateTime,
    handleDateRange, isUsingRHF, formContext, setHasUnsaved, readOnly, theme.palette.text.primary, dateFormatDisplay, 
      dateFormatInput, forceFormData])

    const groupedFields = useMemo(() => {
      const groups = []
      let currentGroup = []

      // Helper function to check if a field should be hidden based on conditions
      const shouldHide = (field) => {
        if (field.condition && (typeof field.condition === 'function')) {
          return !field.condition(
            isUsingRHF ? formContext.getValues() : formData,
            formContext?.getValues(arrayKey) || null,
          )
        } else {
          return false
        }
      }

      fields.forEach((field, index) => {
        const type = field.type.trim().toLowerCase()
        if (!shouldHide(field)) {

          if (type === 'new') {
            groups.push(
              <Grid
                container
                key={groups.length}
                spacing={spacing}
              >
                {currentGroup}
              </Grid>,
            )
            currentGroup = []
          } else {
            currentGroup.push(renderField(field))
          }
        }
      })

      groups.push(
        <Grid
          container
          key={groups.length}
          spacing={spacing}
        >
          {currentGroup}
        </Grid>,
      )

      return groups
    }, [fields, formData, renderField, formContext, isUsingRHF, arrayKey, spacing])

    return <Stack spacing={spacing} m={spacing * -.5}>
      {groupedFields}
      <FileUploadModal
        {...fileUpload?.fileUploadProps}
        open={!!fileUpload}
        onClose={() => setFileUpload(null)}
        formData={fileUpload}
        onFileSelect={handleFile}
      />
    </Stack>
  },
)

export default RenderFormFields
